import type { PodgladSondyDto, StanSystemuDto, WynikSondyDto } from "@shared/wywolaniaTypy.js";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useZasob } from "../../hooks/useZasob.js";
import { api, ApiError } from "../../lib/api.js";
import {
  adresBledowZDoby,
  formatujCzas,
  formatujMs,
  formatujSekundy,
  komunikatBleduApi,
  liczbaZOdmiana,
  liczbaWpisow,
  opisWynikuSondy,
  trescPotwierdzeniaSondy,
} from "../../lib/developer.js";
import { etykietaDostawcy, formatujLiczbe } from "../../lib/koszty.js";
import { Badge } from "../ui/Badge.js";
import { Banner } from "../ui/Banner.js";
import { Button } from "../ui/Button.js";
import { Card } from "../ui/Card.js";
import { ConfirmDialog } from "../ui/ConfirmDialog.js";
import { KpiWartosc } from "../ui/KpiTile.js";
import { StanBledu, StanLadowania } from "../ui/stany.js";
import { BladOdswiezania } from "./BladOdswiezania.js";

/**
 * Zakladka "Stan systemu": wersja, ffmpeg, kolejka, wybor dostawcow i wywolania z ostatniej doby. Wylacznie odczyt i nic
 * platnego: dostawcow nie odpytujemy. Odpytuje ich osobna sonda na dole, ktora jest PLATNA i przechodzi przez potwierdzenie
 * z podana liczba zapytan.
 */

const WARSTWY: readonly { klucz: "transkrypcja" | "brzmienie" | "wymiary"; etykieta: string }[] = [
  { klucz: "transkrypcja", etykieta: "Transkrypcja" },
  { klucz: "brzmienie", etykieta: "Ocena brzmienia" },
  { klucz: "wymiary", etykieta: "Wymiary głosu (SER)" },
];

function Liczniki({ wartosci }: { wartosci: Record<string, number> }): React.JSX.Element {
  const wpisy = Object.entries(wartosci).sort(([a], [b]) => a.localeCompare(b));
  if (wpisy.length === 0) return <span className="developer-meta">brak</span>;
  return (
    <>
      {wpisy.map(([status, ile], i) => (
        <span key={status}>
          {i > 0 ? ", " : ""}
          {status}: <strong>{formatujLiczbe(ile)}</strong>
        </span>
      ))}
    </>
  );
}

function bladSondy(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "za_czesto") {
      const sekund = Number(err.szczegoly.sekund);
      return `Sonda była uruchomiona przed chwilą. Poczekaj ${Number.isFinite(sekund) ? sekund : 10} s i spróbuj ponownie.`;
    }
    if (err.code === "sonda_w_toku") return komunikatBleduApi(err.code);
    return `Sonda nie powiodła się. ${komunikatBleduApi(err.code)}`;
  }
  return `Sonda nie powiodła się. ${komunikatBleduApi("siec")}`;
}

function SondaDostawcow(): React.JSX.Element {
  const [podglad, setPodglad] = useState<PodgladSondyDto | null>(null);
  const [pytam, setPytam] = useState(false);
  const [sprawdzam, setSprawdzam] = useState(false);
  const [wynik, setWynik] = useState<WynikSondyDto | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const [otwieranie, setOtwieranie] = useState(false);

  async function otworzPotwierdzenie(): Promise<void> {
    setBlad(null);
    setOtwieranie(true);
    try {
      setPodglad(await api.developerSondaPodglad());
      setPytam(true);
    } catch (err) {
      setBlad(`Nie udało się sprawdzić, ilu dostawców zostanie odpytanych. ${komunikatBleduApi(err instanceof ApiError ? err.code : "siec")}`);
    } finally {
      setOtwieranie(false);
    }
  }

  async function uruchom(): Promise<void> {
    setSprawdzam(true);
    try {
      setWynik(await api.developerSonda());
      setPytam(false);
      setBlad(null);
    } catch (err) {
      setPytam(false);
      setBlad(bladSondy(err));
    } finally {
      setSprawdzam(false);
    }
  }

  return (
    <Card tytul="Sonda dostawców" akcje={<Badge wariant="warning">częściowo płatna</Badge>}>
      <p className="developer-meta">
        Sprawdza, czy każdy skonfigurowany dostawca faktycznie odpowiada, przez prawdziwą ścieżkę wywołania. Transkrypcja
        (ElevenLabs) i Gemini to płatne wywołania modeli, a OpenAI, Qwen i SER to darmowe sprawdzenie łączności. Wynik zapisuje się w
        historii jako etap "Diagnostyka".
      </p>
      <Button zajety={otwieranie} onClick={() => void otworzPotwierdzenie()}>
        Sprawdź dostawców
      </Button>
      {blad && (
        <p className="developer-blad" role="alert">
          {blad}
        </p>
      )}
      {wynik && (
        <div className="tabela-danych-wrap">
          <table className="tabela-danych" role="table" aria-label="Wynik sondy dostawców">
            <thead role="rowgroup">
              <tr role="row">
                <th role="columnheader">Dostawca</th>
                <th role="columnheader">Stan</th>
                <th role="columnheader" className="is-num">
                  Czas odpowiedzi
                </th>
              </tr>
            </thead>
            <tbody role="rowgroup">
              {wynik.dostawcy.map((d) => (
                <tr role="row" key={`${d.rodzaj}-${d.id}`}>
                  <td role="cell" data-etykieta="Dostawca">
                    <span className="tabela-danych__glowna">{d.etykieta}</span>
                    <span className="tabela-danych__podpis">{d.model}</span>
                  </td>
                  <td role="cell" data-etykieta="Stan">
                    <span className="tabela-danych__wartosc">
                      <span>
                        <Badge wariant={d.stan === "odpowiada" ? "success" : d.stan === "blad" ? "danger" : "neutralny"}>
                          {d.stan === "odpowiada" ? "Odpowiada" : d.stan === "blad" ? "Błąd" : "Nieskonfigurowany"}
                        </Badge>
                      </span>
                      <span className="tabela-danych__podpis">{d.szczegol}</span>
                    </span>
                  </td>
                  <td role="cell" className="is-num" data-etykieta="Czas odpowiedzi">
                    <span className="tabela-danych__wartosc">
                      <span>{formatujMs(d.czasMs)}</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="developer-meta">
            {opisWynikuSondy(wynik)}. Wynik z {formatujCzas(wynik.wygenerowano)}.
          </p>
        </div>
      )}

      <ConfirmDialog
        otwarty={pytam}
        tytul="Odpytać dostawców modeli?"
        // Liczby (zapytania, dostawcy, platne) idą z podgladu serwera: ten sam dostawca bywa sprawdzany w wiecej niz jednej roli.
        tresc={podglad ? trescPotwierdzeniaSondy(podglad) : ""}
        etykietaPotwierdzenia="Wyślij zapytania"
        zajety={sprawdzam}
        onPotwierdz={() => void uruchom()}
        onAnuluj={() => setPytam(false)}
      />
    </Card>
  );
}

function Niedostepny({ nazwa }: { nazwa: string }): React.JSX.Element {
  return (
    <p className="developer-blad" role="status">
      {nazwa}: nie udało się odczytać z bazy przy tym odświeżeniu. To brak odpowiedzi, a nie "zero" ani "pusto". Odśwież widok albo
      sprawdź w logach, czy baza odpowiada.
    </p>
  );
}

function Widok({ s, blad, onPonow }: { s: StanSystemuDto; blad: string | null; onPonow: () => void }): React.JSX.Element {
  const luki = s.lukiZapisu.liczba;
  const { kolejka, wybor, wywolania24h } = s;
  return (
    <>
      {luki > 0 && (
        <Banner
          wariant="warning"
          tekst={`W tym procesie przepadło ${liczbaWpisow(luki)} o wywołaniach (baza niedostępna przy zapisie). Historia może mieć luki od ${formatujCzas(s.lukiZapisu.pierwsza)}.`}
        />
      )}
      {blad !== null && (
        <BladOdswiezania czego="stanu" blad={blad} ostatnie={`Widać ostatnio pobrane dane z ${formatujCzas(s.wygenerowano)}.`} onPonow={onPonow} />
      )}

      <Card tytul="System">
        <dl className="developer-lista">
          <dt>Wersja</dt>
          <dd>{s.wersja}</dd>
          <dt>Środowisko</dt>
          <dd>{s.srodowisko}</dd>
          <dt>Czas działania procesu</dt>
          <dd>{formatujSekundy(s.uptimeSek)}</dd>
          <dt>ffmpeg i ffprobe</dt>
          <dd>
            <Badge wariant={s.ffmpeg.ffmpeg ? "success" : "danger"}>{s.ffmpeg.ffmpeg ? "ffmpeg działa" : "brak ffmpeg"}</Badge>{" "}
            <Badge wariant={s.ffmpeg.ffprobe ? "success" : "danger"}>{s.ffmpeg.ffprobe ? "ffprobe działa" : "brak ffprobe"}</Badge>
          </dd>
          <dt>Retencja treści wywołań</dt>
          <dd>{liczbaZOdmiana(s.retencjaDni, "dzień", "dni", "dni")} (metadane i sumy kontrolne zostają)</dd>
          <dt>Luki zapisu dziennika</dt>
          <dd>{luki === 0 ? "brak w tym procesie" : `${formatujLiczbe(luki)} (pierwsza ${formatujCzas(s.lukiZapisu.pierwsza)}, ostatnia ${formatujCzas(s.lukiZapisu.ostatnia)})`}</dd>
        </dl>
      </Card>

      <Card tytul="Wywołania z ostatniej doby">
        {wywolania24h === null ? (
          <Niedostepny nazwa="Wywołania z ostatniej doby" />
        ) : (
          <>
            <div className="metryki">
              <div>
                <KpiWartosc etykieta="Wszystkie" wartosc={formatujLiczbe(wywolania24h.wszystkie)} />
              </div>
              <div>
                <KpiWartosc etykieta="Błędy" wartosc={formatujLiczbe(wywolania24h.bledy)} />
              </div>
              <div>
                <KpiWartosc etykieta="Porzucone" wartosc={formatujLiczbe(wywolania24h.porzucone)} />
              </div>
            </div>
            <p className="developer-meta">
              {/* Link niesie okno kafelka (`od`): bez niego lista liczylaby bledy z calego zapisu, a nie z tej doby. */}
              <Link to={adresBledowZDoby(s.wygenerowano)}>Pokaż błędy z tej doby w historii</Link>
            </p>
          </>
        )}
      </Card>

      <Card tytul="Kolejka analiz">
        {kolejka === null ? (
          <Niedostepny nazwa="Kolejka analiz" />
        ) : (
          <>
            {kolejka.porzucone.length > 0 && (
              <p className="developer-blad" role="status">
                Zadania bez odświeżenia dłużej niż {formatujSekundy(kolejka.progPorzuceniaSek)} (wyglądają na pracujące, a nikt nad nimi
                nie pracuje): {kolejka.porzucone.map((z) => z.sesjaId).join(", ")}.
              </p>
            )}
            <dl className="developer-lista">
              <dt>Zadania</dt>
              <dd>
                <Liczniki wartosci={kolejka.zadania} />
              </dd>
              <dt>Analizy wg statusu</dt>
              <dd>
                <Liczniki wartosci={kolejka.sesje} />
              </dd>
              <dt>Najstarsze czekające zadanie</dt>
              <dd>
                {kolejka.najstarszeCzekajace
                  ? `czeka ${formatujSekundy(kolejka.najstarszeCzekajace.czekaSek)}, sesja ${kolejka.najstarszeCzekajace.sesjaId}, próby: ${kolejka.najstarszeCzekajace.proby}`
                  : "brak (nic nie czeka)"}
              </dd>
            </dl>
          </>
        )}
      </Card>

      <Card tytul="Wybór dostawców">
        {wybor === null ? (
          <Niedostepny nazwa="Wybór dostawców" />
        ) : (
          <>
            <div className="tabela-danych-wrap">
              <table className="tabela-danych" role="table" aria-label="Wybór dostawców modeli">
                <thead role="rowgroup">
                  <tr role="row">
                    <th role="columnheader">Warstwa</th>
                    <th role="columnheader">Zamówiony</th>
                    <th role="columnheader">Policzy</th>
                    <th role="columnheader">Wersja</th>
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {WARSTWY.map(({ klucz, etykieta }) => {
                    const w = wybor[klucz];
                    return (
                      <tr role="row" key={klucz}>
                        <td role="cell" data-etykieta="Warstwa">
                          <span className="tabela-danych__glowna">{etykieta}</span>
                          {!w.zgodne && (
                            <span className="tabela-danych__podpis">
                              <Badge wariant="warning">{w.podmienioneZ ? "podmieniony" : "niedostępny"}</Badge>
                            </span>
                          )}
                        </td>
                        <td role="cell" data-etykieta="Zamówiony">
                          <span className="tabela-danych__wartosc">
                            <span>{etykietaDostawcy(w.zamowiony)}</span>
                          </span>
                        </td>
                        <td role="cell" data-etykieta="Policzy">
                          <span className="tabela-danych__wartosc">
                            <span>{w.policzy ? etykietaDostawcy(w.policzy) : "nikt (warstwa wyłączona)"}</span>
                          </span>
                        </td>
                        <td role="cell" data-etykieta="Wersja">
                          <span className="tabela-danych__wartosc">
                            <span>{w.wersja ?? "-"}</span>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="developer-meta">
              "Zamówiony" to wybór z Ustawień, "Policzy" to dostawca, który faktycznie odpowie (gdy zamówiony nie ma klucza, wchodzi
              następny dostępny).
            </p>
          </>
        )}
      </Card>

      <SondaDostawcow />
      <p className="developer-meta">Stan z {formatujCzas(s.wygenerowano)}.</p>
    </>
  );
}

export function StanSystemuWidok(): React.JSX.Element {
  const { dane, ladowanie, blad, odswiez } = useZasob("stan", () => api.developerStan(), { co: 30_000 });
  if (blad !== null && dane === null && !ladowanie) {
    return (
      <Card tytul="Stan systemu">
        <StanBledu
          tytul="Nie udało się wczytać stanu systemu"
          opis={komunikatBleduApi(blad)}
          akcja={<Button onClick={odswiez}>Spróbuj ponownie</Button>}
        />
      </Card>
    );
  }
  if (dane === null) {
    return (
      <Card tytul="Stan systemu">
        <StanLadowania tekst="Wczytuję stan systemu..." />
      </Card>
    );
  }
  // Nieudane (takze ciche) odswiezenie przy istniejacych danych nie moze zamrozic widoku bez sygnalu: dane zostaja,
  // a nad nimi stoi komunikat z godzina ostatniego udanego odczytu.
  return <Widok s={dane} blad={blad} onPonow={odswiez} />;
}
